import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { AvatarFrame } from './AvatarFrame';
import { TitleBadge } from './TitleBadge';
import type { FrameType } from './AvatarFrame';
import type { TitleType } from './TitleBadge';
import type { LevelInfo } from '../../services/progressionService';
import * as progressionService from '../../services/progressionService';
import './AchievementCard.css';

interface AchievementCardProps {
    levelInfo: LevelInfo;
    avatarUrl?: string;
    displayName?: string;
    onAvatarClick?: () => void;
}

interface CardTheme {
    background: string;
    text: string;
    subtext: string;
    barTrack: string;
}

const CARD_THEMES: Record<string, CardTheme> = {
    none: {
        background: 'linear-gradient(150deg, #1B4332 0%, #2D6A4F 40%, #40916C 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.72)',
        barTrack: 'rgba(255,255,255,0.18)',
    },
    bronze: {
        background: 'linear-gradient(150deg, #5C3D2E 0%, #8B5E3C 40%, #C68B59 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.72)',
        barTrack: 'rgba(255,255,255,0.2)',
    },
    silver: {
        background: 'linear-gradient(150deg, #4A5568 0%, #718096 40%, #A0AEC0 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.72)',
        barTrack: 'rgba(255,255,255,0.2)',
    },
    gold: {
        background: 'linear-gradient(150deg, #7B5B1E 0%, #B8860B 40%, #DAA520 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.75)',
        barTrack: 'rgba(255,255,255,0.22)',
    },
    platinum: {
        background: 'linear-gradient(150deg, #2D3748 0%, #4A5568 40%, #8896AB 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.72)',
        barTrack: 'rgba(255,255,255,0.18)',
    },
    diamond: {
        background: 'linear-gradient(150deg, #1A3A4A 0%, #2B6CB0 35%, #63B3ED 70%, #9F7AEA 100%)',
        text: '#ffffff',
        subtext: 'rgba(255,255,255,0.75)',
        barTrack: 'rgba(255,255,255,0.2)',
    },
};

export const AchievementCard: React.FC<AchievementCardProps> = ({
    levelInfo,
    avatarUrl,
    displayName,
    onAvatarClick,
}) => {
    const { t } = useTranslation('progression');
    const cardRef = useRef<HTMLDivElement>(null);
    const [sharing, setSharing] = useState(false);

    const theme = CARD_THEMES[levelInfo.activeFrame] || CARD_THEMES.none;
    const xpRemaining = levelInfo.xpNeededForNextLevel - levelInfo.xpInCurrentLevel;
    const showTitleBadge = levelInfo.activeTitle !== 'beginner';

    const handleShare = useCallback(async () => {
        if (!cardRef.current || sharing) return;
        try {
            setSharing(true);
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: null,
                scale: 2,
                useCORS: true,
                logging: false,
            });
            canvas.toBlob(async (blob) => {
                if (!blob) { setSharing(false); return; }
                const file = new File([blob], 'loopy-achievement.png', { type: 'image/png' });
                if (navigator.share && navigator.canShare?.({ files: [file] })) {
                    try {
                        await navigator.share({
                            title: t('share.title'),
                            text: t('share.text', { level: levelInfo.level }),
                            files: [file],
                        });
                    } catch (err) {
                        if ((err as Error).name !== 'AbortError') downloadImage(blob);
                    }
                } else {
                    downloadImage(blob);
                }
                setSharing(false);
            }, 'image/png');
        } catch (err) {
            console.error('Failed to generate share image:', err);
            setSharing(false);
        }
    }, [sharing, levelInfo.level, t]);

    const downloadImage = (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'loopy-achievement.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="achievement-card-wrapper">
            <div
                className="achievement-card"
                ref={cardRef}
                style={{ background: theme.background }}
            >
                <div className="achievement-card-pattern" />

                <div className="achievement-card-content">
                    {/* Avatar */}
                    <div className="achievement-avatar-area">
                        <button
                            className="achievement-avatar-btn"
                            onClick={onAvatarClick}
                            aria-label={t('customize.openCustomize')}
                            type="button"
                        >
                            <AvatarFrame
                                frame={levelInfo.activeFrame as FrameType}
                                avatarUrl={avatarUrl}
                                size={72}
                            />
                            <div className="achievement-avatar-edit-hint">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M11.5 1.5L14.5 4.5L5 14H2V11L11.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </button>
                        {showTitleBadge && (
                            <div className="achievement-title-badge">
                                <TitleBadge title={levelInfo.activeTitle as TitleType} size={28} />
                            </div>
                        )}
                    </div>

                    {/* Level & Name */}
                    <div className="achievement-info" style={{ color: theme.text }}>
                        {displayName && (
                            <div className="achievement-name" style={{ color: theme.subtext }}>{displayName}</div>
                        )}
                        <div className="achievement-level-row">
                            <span className="achievement-level-label" style={{ color: theme.subtext }}>
                                {t('level.level')}
                            </span>
                            <span className="achievement-level-number">{levelInfo.level}</span>
                            <span className="achievement-title-name" style={{ color: theme.subtext }}>
                                {t(`titles.${levelInfo.activeTitle}`)}
                            </span>
                        </div>
                    </div>

                    {/* XP Progress — prominent */}
                    <div className="achievement-xp-section">
                        <div className="achievement-xp-bar-track" style={{ background: theme.barTrack }}>
                            <div
                                className="achievement-xp-bar-fill"
                                style={{ width: `${Math.max(levelInfo.progressPercent, 2)}%` }}
                            />
                        </div>
                        <div className="achievement-xp-text" style={{ color: theme.subtext }}>
                            {levelInfo.level >= 25 ? (
                                <span>{t('level.maxLevel')}</span>
                            ) : (
                                <span>
                                    {t('level.xpToNext', { xp: progressionService.formatXp(xpRemaining), level: levelInfo.level + 1 })}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Small Loopy icon watermark */}
                    <svg className="achievement-watermark-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'rgba(255,255,255,0.15)' }}>
                        <path d="M12 2C9.5 2 7.5 4 7.5 6.5c0 1.5.7 2.8 1.8 3.7C7.2 11.3 6 13.5 6 16c0 3.3 2.7 6 6 6s6-2.7 6-6c0-2.5-1.2-4.7-3.3-5.8 1.1-.9 1.8-2.2 1.8-3.7C16.5 4 14.5 2 12 2z"/>
                    </svg>
                </div>
            </div>

            {/* Share button — solid green, prominent */}
            <button
                className="achievement-share-btn"
                onClick={handleShare}
                disabled={sharing}
                aria-label={t('share.button')}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                <span>{sharing ? t('share.sharing') : t('share.button')}</span>
            </button>
        </div>
    );
};

export default AchievementCard;
